import { h } from '../dom.ts';
import type { TableCtx } from '../games.ts';
import { rattle } from '../sound.ts';

/** Which of the 9 grid cells (row-major) carry a pip for each face. */
const PIPS: Readonly<Record<number, readonly number[]>> = {
  1: [4],
  2: [0, 8],
  3: [0, 4, 8],
  4: [0, 2, 6, 8],
  5: [0, 2, 4, 6, 8],
  6: [0, 2, 3, 5, 6, 8],
};

export interface DieOpts {
  readonly held?: boolean | undefined;
  readonly selected?: boolean | undefined;
  readonly onclick?: (() => void) | undefined;
  readonly small?: boolean | undefined;
  readonly dim?: boolean | undefined;
  readonly testid?: string | undefined;
  readonly tag?: string | undefined;
}

/** A six-sided die drawn with CSS pips. A face outside 1-6 renders a blank die. */
export function die(face: number, opts: DieOpts = {}): HTMLElement {
  const on = new Set(PIPS[face] ?? []);
  const cls = ['die', opts.held ? 'held' : '', opts.selected ? 'selected' : '', opts.small ? 'small' : '', opts.dim ? 'dim' : '', PIPS[face] ? '' : 'blank'].filter(Boolean).join('.');
  return h(
    `${opts.onclick ? 'button' : 'div'}.${cls}`,
    {
      'data-face': face,
      'data-testid': opts.testid,
      'aria-label': PIPS[face] ? `die showing ${face}${opts.held ? ', held' : ''}${opts.selected ? ', selected' : ''}` : 'unrolled die',
      'aria-pressed': opts.onclick && (opts.held !== undefined || opts.selected !== undefined) ? String(!!(opts.held || opts.selected)) : undefined,
      onclick: opts.onclick ? () => opts.onclick?.() : undefined,
    },
    Array.from({ length: 9 }, (_, i) => h(`span.pip${on.has(i) ? '.on' : ''}`)),
    opts.tag ? h('span.die-tag', {}, opts.tag) : null,
  );
}

/** A face-down cup with a dice count. */
export function cup(count: number, opts: { testid?: string; label?: string } = {}): HTMLElement {
  return h('div.cup', { 'data-testid': opts.testid, 'data-count': count, 'aria-label': opts.label ?? `cup with ${count} dice` }, h('span.cup-n', {}, count));
}

const ROLL_MS = 700;
const TICK_MS = 70;

/**
 * New dice tumble through random faces for everyone at the table before they land (FR-DICE-01).
 * `key` changes whenever the dice change; `fresh` says the change was a roll (not a join or a score).
 * The first render after joining never animates. True while the tumble is running.
 */
export function tumbling(ctx: TableCtx, id: string, key: string, fresh: boolean): boolean {
  const seenKey = `${id}-seen`;
  const untilKey = `${id}-until`;
  const seen = ctx.local.get<string>(seenKey);
  if (seen !== key) {
    ctx.local.set(seenKey, key);
    if (seen !== undefined && fresh) {
      rattle();
      if (!matchMedia('(prefers-reduced-motion: reduce)').matches) ctx.local.set(untilKey, Date.now() + ROLL_MS);
    }
  }
  const until = ctx.local.get<number>(untilKey) ?? 0;
  if (Date.now() >= until) return false;
  setTimeout(() => ctx.local.set(`${id}-tick`, Date.now()), TICK_MS);
  return true;
}

/** A random face, for dice mid-tumble. */
export const randomFace = (): number => 1 + Math.floor(Math.random() * 6);
